import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, MessageSquarePlus, QrCode } from "lucide-react";
import { Stars } from "../components/Stars";
import { ReviewDialog } from "../components/ReviewDialog";
import { QrDialog } from "../components/QrDialog";
import axios, { API, formatDate, photoSrc } from "../lib/api";

const Breakdown = ({ washer }) => (
  <div className="space-y-2" data-testid="rating-breakdown">
    {[5, 4, 3, 2, 1].map((n) => {
      const c = washer.breakdown[n] || 0;
      const pct = washer.review_count ? (c / washer.review_count) * 100 : 0;
      return (
        <div key={n} className="flex items-center gap-3 text-sm">
          <span className="w-3 text-slate-400">{n}</span>
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-white/5">
            <div className="h-full rounded-full bg-[#FFB800]" style={{ width: `${pct}%` }} />
          </div>
          <span className="w-6 text-right text-slate-500">{c}</span>
        </div>
      );
    })}
  </div>
);

const ReviewItem = ({ r }) => (
  <li data-testid={`review-card-${r.id}`} className="rounded-2xl border border-white/[0.07] bg-[#131B26] p-5">
    <div className="flex items-center justify-between gap-3">
      <span className="font-semibold">{r.author || "Client Anonim"}</span>
      <span className="text-xs text-slate-500">{formatDate(r.created_at)}</span>
    </div>
    <div className="mt-2"><Stars value={r.rating} size={15} /></div>
    {r.comment && <p className="mt-3 text-slate-300">{r.comment}</p>}
  </li>
);

export default function WasherPage() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const [washer, setWasher] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [missing, setMissing] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(params.get("recenzie") === "1");
  const [qrOpen, setQrOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [w, r] = await Promise.all([axios.get(`${API}/washers/${id}`), axios.get(`${API}/washers/${id}/reviews`)]);
      setWasher(w.data); setReviews(r.data);
    } catch { setMissing(true); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const closeReview = (o) => { setReviewOpen(o); if (!o && params.get("recenzie")) setParams({}, { replace: true }); };

  if (missing) return <p className="mx-auto max-w-6xl px-4 py-20 text-slate-400" data-testid="washer-not-found">Spălătorul nu a fost găsit.</p>;
  if (!washer) return <p className="mx-auto max-w-6xl px-4 py-20 text-slate-500">Se încarcă…</p>;

  return (
    <main className="mx-auto max-w-6xl px-4 pb-36 pt-8 sm:px-6">
      <Link to="/" data-testid="back-to-team-link" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white">
        <ArrowLeft size={16} /> Toată echipa
      </Link>
      <div className="mt-6 grid grid-cols-1 items-start gap-10 lg:grid-cols-12">
        <aside className="rise space-y-6 lg:sticky lg:top-24 lg:col-span-5">
          <div className="overflow-hidden rounded-3xl border border-white/[0.07] bg-slate-800">
            {washer.photo_url && <img src={photoSrc(washer.photo_url)} alt={washer.name} data-testid="washer-detail-photo" className="aspect-[4/3] w-full object-cover" />}
          </div>
          <div>
            <h1 className="font-display text-4xl font-bold tracking-tight sm:text-5xl" data-testid="washer-detail-name">{washer.name}</h1>
            {washer.bio && <p className="mt-3 text-slate-400" data-testid="washer-detail-bio">{washer.bio}</p>}
          </div>
          <div className="flex items-center gap-4 rounded-2xl border border-white/[0.07] bg-[#131B26] p-5">
            <span className="font-display text-5xl font-bold" data-testid="washer-detail-avg">{washer.review_count ? washer.avg_rating.toFixed(1) : "–"}</span>
            <div className="flex-1 space-y-1">
              <Stars value={washer.avg_rating} size={18} />
              <p className="text-sm text-slate-500" data-testid="washer-detail-count">{washer.review_count} {washer.review_count === 1 ? "recenzie" : "recenzii"}</p>
            </div>
          </div>
          <Breakdown washer={washer} />
          <button onClick={() => setQrOpen(true)} data-testid="open-qr-btn"
            className="flex w-full items-center justify-center gap-2 rounded-full border border-white/10 py-3 text-sm font-medium text-slate-300 transition-colors hover:border-[#00F0FF]/60 hover:text-white">
            <QrCode size={17} /> Cod QR pentru recenzii
          </button>
        </aside>
        <section className="lg:col-span-7">
          <h2 className="font-display text-base font-semibold uppercase tracking-[0.2em] text-slate-400 md:text-lg">Recenzii</h2>
          {reviews.length === 0 ? (
            <p className="mt-6 text-slate-400" data-testid="reviews-empty">Încă nu există recenzii. Fii primul!</p>
          ) : (
            <ul className="mt-6 space-y-4" data-testid="reviews-list">
              {reviews.map((r) => <ReviewItem key={r.id} r={r} />)}
            </ul>
          )}
        </section>
      </div>
      <button onClick={() => setReviewOpen(true)} data-testid="open-add-review-modal-btn"
        className="fixed bottom-5 left-1/2 z-30 flex -translate-x-1/2 items-center gap-2 whitespace-nowrap rounded-full bg-[#00F0FF] px-7 py-4 font-semibold text-[#090E17] shadow-[0_10px_40px_-10px_rgba(0,240,255,0.6)] transition-colors hover:bg-[#33F3FF]">
        <MessageSquarePlus size={19} /> Lasă o recenzie
      </button>
      <ReviewDialog washer={washer} open={reviewOpen} onOpenChange={closeReview} onDone={load} />
      <QrDialog washer={washer} open={qrOpen} onOpenChange={setQrOpen} />
    </main>
  );
}
